// `/forgot-password` on the admin host: spec 15 §4 S1a and §3.1 step 7 (D-587).
//
// ONE POST-SUBMIT STATE, BY CONTRACT. `POST /v1/platform/auth/password-reset`
// answers the same empty 202 for a staff address, a stranger's, a disabled
// member's and a throttled request, so this screen has exactly one success
// state, and its copy says the conditional out loud ("if that address has a
// console account") instead of pretending to confirm one. The only failure it
// can show is the request not being taken at all: a malformed address the
// input let through, a network failure, or a server error.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { AdminAuthCard } from "../admin-shell";
import { requestPasswordReset } from "../lib/platform-api";

export function AdminForgotPasswordScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const request = useMutation({
    mutationFn: () => requestPasswordReset(email.trim()),
    onSuccess: () => {
      setSent(true);
    },
  });

  const backToSignIn = (
    <Button
      size="sm"
      onClick={() => {
        void navigate({ to: "/login" });
      }}
      data-testid="admin-forgot-back"
    >
      {t("admin.forgot.backToSignIn")}
    </Button>
  );

  if (sent) {
    return (
      <AdminAuthCard
        title={t("admin.forgot.sentTitle")}
        lead={t("admin.forgot.sent")}
        data-testid="admin-forgot"
      >
        <Banner kind="ok" data-testid="admin-forgot-sent">
          {t("admin.forgot.sentNote")}
        </Banner>
        <div className="admin-actions">{backToSignIn}</div>
      </AdminAuthCard>
    );
  }

  return (
    <AdminAuthCard
      title={t("admin.forgot.title")}
      lead={t("admin.forgot.lead")}
      data-testid="admin-forgot"
    >
      {request.isError ? (
        <Banner kind="bad" data-testid="admin-forgot-error">
          {t("admin.forgot.error")}
        </Banner>
      ) : null}
      <form
        className="admin-form"
        data-testid="admin-forgot-form"
        onSubmit={(e) => {
          e.preventDefault();
          request.mutate();
        }}
      >
        <div className="ds-field">
          <label htmlFor="admin-forgot-email">{t("admin.forgot.email")}</label>
          <input
            id="admin-forgot-email"
            className="ds-input"
            type="email"
            autoComplete="username"
            spellCheck={false}
            autoCapitalize="off"
            value={email}
            data-testid="admin-forgot-email"
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            required
          />
        </div>
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={request.isPending}
            data-testid="admin-forgot-submit"
          >
            {request.isPending ? t("admin.forgot.submitting") : t("admin.forgot.submit")}
          </Button>
          {backToSignIn}
        </div>
      </form>
    </AdminAuthCard>
  );
}
